// src/components/analytics/DashboardAIInsights.js
// --- UPDATE (round 3: real data, working actions) ------------------------------------------------
// • Confidence badges were fixed numbers (90 %, 95 %, 98 %, 100 %) for rule-based findings. A rule
//   has no confidence — each card now shows its basis instead ("2.3 σ above the 6-month mean",
//   "3 production models"). Only the cost forecast shows a confidence, the model's own average.
// • The "stale model" check ran over every registry row (archived versions made it fire forever);
//   it now looks only at production versions. aiHealth.status is 'ok' | 'degraded' | 'offline' —
//   'degraded' used to be reported as "AI System Offline".
// • Missing KPIs are no longer treated as 100 % uptime / 0 alerts; the KPI insight only runs on
//   real values, and uses the real systemUptime_trend (vs the previous period).
// • Action buttons did nothing. They now navigate (AI Intelligence Center, the right Analytics tab)
//   or ask FlowSense AI about the finding (onAskAI); a parent can override with onAction(insight).
// • Optional serverInsights (GET /analytics/ai-insights) are merged in, with their evidence.
// --- END UPDATE ------------------------------------------------------------------------------------
import React, { useMemo, useCallback } from 'react';
import {
  Box, VStack, HStack, Heading, Text, Badge, Button, Tooltip,
  useColorModeValue, Icon,
} from '@chakra-ui/react';
import { motion } from 'framer-motion';
import {
  MdInsights, MdSmartToy, MdModelTraining, MdError, MdCheckCircle,
  MdChevronRight, MdTrendingUp, MdTrendingDown, MdWarningAmber,
} from 'react-icons/md';
import { FaBrain, FaFireAlt, FaRobot, FaBolt, FaChartLine } from 'react-icons/fa';
import { useNavigate } from 'react-router-dom';

const MotionBox = motion(Box);
const STALE_DAYS = 30;

const mean = (arr) => (arr.length ? arr.reduce((a, b) => a + b, 0) / arr.length : 0);
const stdDev = (arr, m) => (arr.length ? Math.sqrt(arr.map(x => (x - m) ** 2).reduce((a, b) => a + b, 0) / arr.length) : 0);
const numOrNull = (v) => (v === null || v === undefined || v === '' || !Number.isFinite(Number(v)) ? null : Number(v));
const isProduction = (m) => m && (m.status === 'production' || m.status === 'champion' || m.is_active === true || m.is_active === 1);

const TYPE_COLOR = { critical: 'red.500', alert: 'red.400', error: 'red.500', warning: 'orange.500', success: 'green.500', info: 'blue.500', live: 'purple.500' };
const SERVER_ICON = { error: MdError, warning: MdWarningAmber, info: FaRobot, live: FaBolt };

export function buildInsights({ kpis, costData, predictions, aiHealth, aiModels }) {
  const insights = [];

  // 1. AI system health (production models only)
  const status = aiHealth?.status || null;
  if (status === 'ok' || status === 'degraded') {
    const prod = (aiModels || []).filter(isProduction);
    const stale = prod.filter((m) => {
      const t = new Date(m.last_trained_at || m.activated_at || m.created_at).getTime();
      return Number.isFinite(t) && (Date.now() - t) / 86400000 > STALE_DAYS;
    });
    if (status === 'degraded') {
      insights.push({
        id: 'ai-degraded', type: 'warning', icon: MdWarningAmber, title: 'AI services degraded',
        message: aiHealth?.message || 'Part of the AI stack is unavailable; affected predictions use fallbacks.',
        basis: 'AI health check', action: 'Open AI Intelligence', to: '/admin/ai/grading?tab=health',
      });
    }
    if (stale.length > 0) {
      insights.push({
        id: 'ai-stale', type: 'warning', icon: MdModelTraining, title: 'Model retraining due',
        message: `${stale.length} production model${stale.length === 1 ? ' has' : 's have'} not been retrained in ${STALE_DAYS}+ days: ${stale.map(m => m.model_type || m.model_name).join(', ')}.`,
        basis: `${prod.length} production model${prod.length === 1 ? '' : 's'} checked`, action: 'Train models', to: '/admin/ai/grading?tab=training',
      });
    } else if (prod.length > 0 && status === 'ok') {
      insights.push({
        id: 'ai-ok', type: 'success', icon: MdSmartToy, title: 'AI models up to date',
        message: `${prod.length} production model${prod.length === 1 ? ' is' : 's are'} serving predictions, all trained within ${STALE_DAYS} days.`,
        basis: 'model registry', action: 'View registry', to: '/admin/ai/grading?tab=registry',
      });
    } else if (prod.length === 0 && aiModels) {
      insights.push({
        id: 'ai-none', type: 'warning', icon: MdModelTraining, title: 'No model in production',
        message: 'No AI model has a production version yet, so predictions use fallbacks. Train one in the AI Intelligence Center.',
        basis: 'model registry', action: 'Train models', to: '/admin/ai/grading?tab=training',
      });
    }
  } else if (status === 'offline' || status === 'error' || status === 'down') {
    insights.push({
      id: 'ai-offline', type: 'critical', icon: MdError, title: 'AI engine offline',
      message: 'The ML engine is unreachable. Predictions use rule-based fallbacks until it is back.',
      basis: 'AI health check', action: 'Check engine health', to: '/admin/ai/grading?tab=health',
    });
  }

  // 2. Operations (only with real values)
  if (kpis) {
    const uptime = numOrNull(kpis.systemUptime);
    const alerts = numOrNull(kpis.criticalAlerts);
    const trend = numOrNull(kpis.systemUptime_trend);
    if (uptime !== null && alerts !== null && uptime < 95 && alerts > 5) {
      insights.push({
        id: 'ops-instability', type: 'critical', icon: FaBolt, title: 'Instability: alerts and downtime together',
        message: `${alerts} critical alerts are active while only ${uptime}% of assets are operational.`,
        basis: 'live KPIs', action: 'Investigate', ask: `There are ${alerts} critical alerts and ${uptime}% of assets operational. What is the likely root cause and what should we do first?`,
      });
    } else if (trend !== null && trend < -2) {
      insights.push({
        id: 'ops-degrading', type: 'warning', icon: FaBrain, title: 'Asset availability falling',
        message: `Operational assets are ${Math.abs(trend).toFixed(1)}% lower than in the previous period.`,
        basis: 'vs previous period', action: 'See asset performance', tab: 'assets',
      });
    }
  }

  // 3. Cost anomaly (z-score of the latest month against the earlier ones)
  const costs = (costData || []).map(c => numOrNull(c.totalCost ?? c.total_cost ?? c.cost)).filter(v => v !== null);
  if (costs.length > 3) {
    const latest = costs[costs.length - 1];
    const hist = costs.slice(0, -1);
    const m = mean(hist); const sd = stdDev(hist, m);
    const z = sd === 0 ? 0 : (latest - m) / sd;
    if (z > 2) {
      insights.push({
        id: 'cost-anomaly', type: 'alert', icon: FaFireAlt, title: 'Cost anomaly',
        message: `Latest spend ($${Math.round(latest).toLocaleString()}) is far above the earlier average ($${Math.round(m).toLocaleString()}).`,
        basis: `${z.toFixed(1)} σ above the ${hist.length}-period mean`, action: 'Ask why', ask: `Maintenance spend of $${Math.round(latest)} is ${z.toFixed(1)} standard deviations above the average of $${Math.round(m)}. What could explain it?`,
      });
    } else if (z > 1) {
      insights.push({
        id: 'cost-elevated', type: 'warning', icon: FaChartLine, title: 'Costs above average',
        message: `Latest spend is above the ${hist.length}-period average of $${Math.round(m).toLocaleString()}.`,
        basis: `${z.toFixed(1)} σ above the mean`, action: 'See cost breakdown', tab: 'assets',
      });
    }
  }

  // 4. Forecast — confidence is the model's own average, when it gave one
  const preds = (predictions || []).filter(p => numOrNull(p.predicted) !== null);
  if (preds.length > 1) {
    const first = Number(preds[0].predicted); const last = Number(preds[preds.length - 1].predicted);
    const growth = first === 0 ? null : ((last - first) / Math.abs(first)) * 100;
    const confs = preds.map(p => numOrNull(p.confidence)).filter(v => v !== null);
    const confidence = confs.length ? mean(confs) * (mean(confs) <= 1 ? 100 : 1) : null;
    if (growth !== null) {
      const base = { basis: `forecast over ${preds.length} periods`, confidence };
      if (growth > 10) insights.push({ ...base, id: 'fc-surge', type: 'warning', icon: MdTrendingUp, title: 'Cost increase forecast', message: `Costs are forecast to rise ${growth.toFixed(1)}% over the forecast window.`, action: 'Ask how to prepare', ask: `Maintenance costs are forecast to rise ${growth.toFixed(1)}%. Which assets drive this and what can we defer or prevent?` });
      else if (growth < -5) insights.push({ ...base, id: 'fc-saving', type: 'success', icon: MdTrendingDown, title: 'Cost reduction forecast', message: `Costs are forecast to fall ${Math.abs(growth).toFixed(1)}%.`, action: 'See asset performance', tab: 'assets' });
      else insights.push({ ...base, id: 'fc-stable', type: 'info', icon: FaRobot, title: 'Stable cost outlook', message: 'Spending is forecast to stay within ±5–10%.', action: 'See forecast', tab: 'executive' });
    }
  }

  return insights;
}

const DashboardAIInsights = ({ kpis, costData, predictions, aiHealth, aiModels, serverInsights = null, onClose, onAskAI = null, onAction = null }) => {
  const navigate = useNavigate();
  const bgColor = useColorModeValue('white', 'gray.800');
  const borderColor = useColorModeValue('gray.200', 'gray.700');
  const insightBg = useColorModeValue('purple.50', 'purple.900');
  const muted = useColorModeValue('gray.600', 'gray.300');

  const insights = useMemo(() => {
    const local = buildInsights({ kpis, costData, predictions, aiHealth, aiModels });
    const fromServer = (serverInsights || []).map(s => ({
      id: `srv-${s.id}`, type: s.type || 'info', icon: SERVER_ICON[s.type] || MdInsights, title: s.title, message: s.message,
      basis: s.source ? `source: ${String(s.source).replace(/_/g, ' ')}` : null, action: s.action || 'Open', to: s.link || null,
      tab: s.actionPayload?.tab || null,
    }));
    const seen = new Set();
    return [...fromServer, ...local].filter(i => (seen.has(i.title) ? false : (seen.add(i.title), true)));
  }, [kpis, costData, predictions, aiHealth, aiModels, serverInsights]);

  const act = useCallback((insight) => {
    if (onAction) { onAction(insight); return; }
    if (insight.ask && onAskAI) { onAskAI(insight.ask); return; }
    if (insight.tab) { navigate(`/admin/analytics?tab=${insight.tab}`); return; }
    if (insight.to) { navigate(insight.to); return; }
    if (onAskAI) onAskAI(`Explain this finding and what to do: ${insight.title}. ${insight.message}`);
  }, [navigate, onAction, onAskAI]);

  if (insights.length === 0) {
    return (
      <MotionBox initial={{ opacity: 0, scale: 0.95 }} animate={{ opacity: 1, scale: 1 }} bg={bgColor} borderWidth="1px" borderColor={borderColor} borderRadius="lg" p={5} textAlign="center" color="gray.500">
        <Icon as={MdCheckCircle} boxSize={8} color="green.400" mb={2} />
        <Text>No AI findings for this period — the data shows nothing unusual.</Text>
      </MotionBox>
    );
  }

  return (
    <MotionBox initial={{ opacity: 0, scale: 0.95 }} animate={{ opacity: 1, scale: 1 }} bg={bgColor} borderWidth="1px" borderColor={borderColor} borderRadius="lg" p={5} boxShadow="lg">
      <HStack justify="space-between" mb={4}>
        <HStack>
          <Box p={2} bg="purple.500" borderRadius="md"><MdInsights color="white" size={24} /></Box>
          <VStack align="start" spacing={0}>
            <Heading size="md">AI-Powered Insights</Heading>
            <Text fontSize="xs" color="gray.500">From live KPIs, cost history, forecasts and the model registry</Text>
          </VStack>
        </HStack>
        <Badge colorScheme="purple" variant="solid" borderRadius="full" px={3}>{insights.length} active</Badge>
      </HStack>

      <VStack align="stretch" spacing={3}>
        {insights.map((insight, idx) => (
          <MotionBox key={insight.id} initial={{ opacity: 0, x: -20 }} animate={{ opacity: 1, x: 0 }} transition={{ delay: idx * 0.05 }}
            bg={insightBg} p={4} borderRadius="md" borderLeftWidth="4px" borderLeftColor={TYPE_COLOR[insight.type] || 'blue.500'} data-testid={`insight-${insight.id}`}>
            <HStack align="start" spacing={3}>
              <Box color={TYPE_COLOR[insight.type] || 'purple.500'} pt={1}><Icon as={insight.icon} /></Box>
              <VStack align="start" flex={1} spacing={1}>
                <HStack justify="space-between" w="100%" align="flex-start">
                  <Text fontWeight="bold" fontSize="sm">{insight.title}</Text>
                  {insight.confidence !== null && insight.confidence !== undefined ? (
                    <Tooltip label="Average confidence reported by the forecasting model" hasArrow>
                      <Badge colorScheme="purple" variant="subtle">{insight.confidence.toFixed(0)}% conf.</Badge>
                    </Tooltip>
                  ) : insight.basis ? (
                    <Badge colorScheme="gray" variant="subtle" fontWeight="normal" textTransform="none" whiteSpace="normal" textAlign="right">{insight.basis}</Badge>
                  ) : null}
                </HStack>
                <Text fontSize="sm" color={muted}>{insight.message}</Text>
                <Button size="xs" variant="link" colorScheme="purple" rightIcon={<MdChevronRight />} onClick={() => act(insight)}>{insight.action}</Button>
              </VStack>
            </HStack>
          </MotionBox>
        ))}
      </VStack>

      {onClose && <Button mt={4} width="full" size="sm" onClick={onClose}>Close Insights</Button>}
    </MotionBox>
  );
};

export default DashboardAIInsights;
